<template>
  <RoutineSelector
    ref="routineSelector"
    :selectedRoutineIds="selectedRoutineIds"
    @select="handleSelect"
  >
    <template v-slot:activator="{ selectedRoutines }">
      <v-select
        class="caption white explore-routines-filter-activator-container"
        dense
        hide-details
        outlined
        readonly
        ref="routineSelectorActivator"
        :placeholder="$t('menu.selection.select_an_option')"
      >
        <template v-slot:label>
          <div class="white px-1 caption" data-form-type="other">
            {{ $t('routines.routine_other') }}
          </div>
        </template>

        <template v-slot:selection="{ item, index }">
          <div class="d-flex">
            <p
              v-if="index < 2"
              class="ma-0 pa-0 routine-definition-selector-name"
            >
              {{ item.name }}
            </p>
            <span
              v-if="index === 0 && selectedRoutines.length > 1"
              class="ml-0 mr-1"
              >,
            </span>

            <v-tooltip v-if="index === 2" bottom max-width="400px">
              <template v-slot:activator="{ on }">
                <v-chip v-on="on" small color="blue-grey lighten-3">
                  + {{ selectedRoutines.length - 2 }}
                </v-chip>
              </template>

              <ul class="ma-0 pa-0 ms-2">
                <div
                  v-for="(option, index) in selectedRoutines"
                  :key="option.id"
                >
                  <li v-if="index > 1" class="ma-0 pa-0">
                    {{ option.name }}
                  </li>
                </div>
              </ul>
            </v-tooltip>
          </div>
        </template>
      </v-select>
    </template>
  </RoutineSelector>
</template>

<script>
import RoutineSelector from '@/components/RoutineSelector/RoutineSelector.vue'
import {
  finishDeferredInteraction as resolveDeferredInteractionResult,
  startDeferredInteraction as createDeferredInteractionState
} from './deferredInteraction'

export default {
  name: 'RoutineFilter',

  components: {
    RoutineSelector,
  },

  props: {
    emitAutoApplyMetadata: Boolean,
    selectedRoutineIds: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      interactionSequence: 0,
      menuCurrentValue: null,
      menuInitialValue: null,
      menuInteractionId: null,
      menuStateWatcher: null,
      selectionWatcher: null,
    }
  },

  mounted() {
    this.setupMenuStateWatcher()
    this.setupSelectedRoutinesWatcher()
  },

  beforeDestroy() {
    if (this.menuStateWatcher) this.menuStateWatcher()
    if (this.selectionWatcher) this.selectionWatcher()
  },

  methods: {
    buildSelectionPayload(value, overrides = {}) {
      if (!this.emitAutoApplyMetadata) {
        return value
      }

      return {
        key: 'routineDefinitions',
        value,
        ...overrides,
      }
    },

    emitRoutineSelection(value, overrides = {}) {
      this.$emit('select', this.buildSelectionPayload(value, overrides))
    },

    startDeferredInteraction() {
      const deferredState = createDeferredInteractionState({
        interactionSequence: this.interactionSequence,
        prefix: 'routineDefinitions',
        value: this.selectedRoutineIds ?? [],
      })

      this.interactionSequence = deferredState.interactionSequence
      this.menuCurrentValue = deferredState.menuCurrentValue
      this.menuInitialValue = deferredState.menuInitialValue
      this.menuInteractionId = deferredState.menuInteractionId
    },

    resetDeferredInteraction() {
      this.menuCurrentValue = null
      this.menuInitialValue = null
      this.menuInteractionId = null
    },

    ensureDeferredInteraction() {
      if (!this.menuInteractionId) this.startDeferredInteraction()
    },

    emitDeferredRoutineDraft(value) {
      this.ensureDeferredInteraction()
      this.menuCurrentValue = globalThis.structuredClone(value ?? [])

      this.emitRoutineSelection(value, {
        commitMode: 'deferredAfterBlur',
        source: 'user',
        shouldApply: false,
        interactionId: this.menuInteractionId,
      })
    },

    finishDeferredInteraction() {
      if (!this.emitAutoApplyMetadata || !this.menuInteractionId) return

      const { didChange, finalValue } = resolveDeferredInteractionResult({
        fallbackValue: this.selectedRoutineIds ?? [],
        menuCurrentValue: this.menuCurrentValue,
        menuInitialValue: this.menuInitialValue,
      })

      if (!didChange) {
        this.resetDeferredInteraction()
        return
      }

      this.emitRoutineSelection(finalValue, {
        commitMode: 'deferredAfterBlur',
        source: 'user',
        shouldApply: true,
        interactionId: this.menuInteractionId,
      })

      this.resetDeferredInteraction()
    },

    handleMenuStateChange(isOpen) {
      if (!this.emitAutoApplyMetadata) return

      if (isOpen) {
        this.startDeferredInteraction()
        return
      }

      this.finishDeferredInteraction()
    },

    handleSelect(value) {
      if (!this.emitAutoApplyMetadata) {
        this.emitRoutineSelection(value)
        return
      }

      this.emitDeferredRoutineDraft(value)
    },

    setupMenuStateWatcher() {
      if (!this.emitAutoApplyMetadata) return
      if (!this.$refs?.routineSelector || this.menuStateWatcher) return

      this.menuStateWatcher = this.$refs.routineSelector.$watch(
        'openMenu',
        (isOpen, wasOpen) => {
          if (typeof isOpen !== 'boolean' || isOpen === wasOpen) return
          this.handleMenuStateChange(isOpen)
        }
      )
    },

    setupSelectedRoutinesWatcher() {
      this.selectionWatcher = this.$watch(
        () => this.$refs.routineSelector?.selectedRoutines,
        this.syncActivatorSelection,
        { immediate: true }
      )
    },

    syncActivatorSelection(selectedRoutines) {
      this.$refs.routineSelectorActivator.selectedItems = selectedRoutines
    },
  },
}
</script>

<style lang="scss">
.explore-routines-filter-activator-container {
  position: relative;
  height: 32px !important;
  min-height: 32px !important;

  .routine-definition-selector-name {
    max-width: 150px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .v-input__append-inner {
    margin-top: 4px !important;
  }

  .v-input__control .v-input__slot {
    background-color: $color-white;
    height: 32px !important;
    min-height: 32px !important;

    fieldset legend {
      width: fit-content !important;
    }
    .v-select__slot {
      display: flex;
      align-items: center;
      .v-label {
        top: 8px;
        div {
          text-overflow: ellipsis;
          overflow: hidden;
        }
      }
      .v-label--active {
        top: 10px;
      }
      .v-select__selections:has(div) {
        input {
          width: 0px !important;
          min-width: 0px !important;
        }
      }
    }
  }
}
</style>
